import { Video } from "lucide-react";
import {
  RecordContextProvider,
  useCreatePath,
  useGetList,
  useTranslate,
} from "ra-core";
import { Link } from "react-router";
import { DateField } from "@/components/admin/date-field";

import type { ZoomMeetingSummaryView } from "../types";
import {
  refetchWhileAwaiting,
  rowsFromQueryData,
} from "../zoom_meetings/meetingAssets";
import { ReviewStatusField } from "../zoom_meetings/ReviewStatusField";
import { meetingDisplayTitle } from "../zoom_meetings/meetingDisplayTitle";
import { DashboardWidget } from "./DashboardWidget";

const ROW_CLASS =
  "flex items-center gap-2 py-2 first:pt-0 last:pb-0 text-foreground -mx-2 px-2 rounded";

const CallRow = ({ summary }: { summary: ZoomMeetingSummaryView }) => {
  const translate = useTranslate();
  return (
    <>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">
          {meetingDisplayTitle(summary)}
        </div>
        <div className="text-xs text-muted-foreground">
          <DateField source="meeting_start_time" showTime />
          {summary.tasks?.length
            ? ` · ${translate("crm.common.task_count", {
                smart_count: summary.tasks.length,
              })}`
            : ""}
        </div>
      </div>
      <ReviewStatusField />
    </>
  );
};

/**
 * `linkable` is false on the mobile dashboard: the mobile app has no meeting
 * page, so a row there would navigate to a dead route.
 */
export const RecentClientCalls = ({
  linkable = true,
}: {
  linkable?: boolean;
}) => {
  const translate = useTranslate();
  const createPath = useCreatePath();
  const { data, isPending } = useGetList<ZoomMeetingSummaryView>(
    "zoom_meetings",
    {
      pagination: { page: 1, perPage: 20 },
      sort: { field: "meeting_start_time", order: "DESC" },
      filter: { "has_content@eq": true },
    },
    {
      // Just-ended calls are listed while Zoom is still delivering; refresh
      // until they have files or stop waiting.
      refetchInterval: (query) =>
        refetchWhileAwaiting(
          rowsFromQueryData<ZoomMeetingSummaryView>(query.state.data),
        ),
    },
  );

  return (
    <DashboardWidget
      icon={<Video className="text-muted-foreground w-6 h-6" />}
      title={translate("crm.dashboard.recent_client_calls", {
        _: "Recent Client Calls",
      })}
      seeAllTo="/zoom_meetings"
    >
      {isPending ? null : !data?.length ? (
        <p className="text-sm text-muted-foreground">
          {translate("crm.dashboard.no_recent_calls", {
            _: "No client calls yet.",
          })}
        </p>
      ) : (
        <div className="divide-y">
          {data.map((summary) => (
            <RecordContextProvider key={summary.id} value={summary}>
              {linkable ? (
                <Link
                  to={createPath({
                    resource: "zoom_meetings",
                    id: summary.id,
                    type: "show",
                  })}
                  className={`${ROW_CLASS} no-underline hover:bg-muted/50`}
                >
                  <CallRow summary={summary} />
                </Link>
              ) : (
                <div className={ROW_CLASS}>
                  <CallRow summary={summary} />
                </div>
              )}
            </RecordContextProvider>
          ))}
        </div>
      )}
    </DashboardWidget>
  );
};
